/* eslint-disable react-hooks/rules-of-hooks */
import { composer } from '../../../component-decorators/composer';
import type { WithTranslation as WithTranslationProps } from 'react-i18next';
import s from './solution-page.scss';
import React, { useState, useEffect } from 'react';
import { ExternalLink, ConfirmSmall } from '@wix/wix-ui-icons-common';
import {
  Text,
  Heading,
  Card,
  Divider,
  TextButton,
  Box,
  SegmentedToggle,
} from '@wix/design-system';
import type { InjectedExperimentsProps } from '@wix/wix-experiments-react';
import { biLogger } from '../../../bi';
import type {
  Plan,
  WebSolution,
  CurrencySettings,
  TaxSettings,
} from '@wix/ambassador-marketplace/types';
import {
  PlanDescription,
  IntervalUnit,
  TaxSettingsTaxType,
} from '@wix/ambassador-marketplace/types';
import { usePremiumCurrencyFormatter } from '@wix/premium-react-text';
import { isWixApp } from '../../../common/utils';
import { AppPageMode } from '../../../enums/marketplace-enums';
import { useConfigContext } from '../../../config-context';
import { appmarketPackagePickerToggleCycleClick } from '@wix/bi-logger-market/v2';

const enum TogglePricingValues {
  NONE = 'NONE',
  YEARLY = 'YEARLY',
  MONTHLY = 'MONTHLY',
}

export const dtoToSolutionPlansProps = (
  solutionDto: WebSolution,
  mode: AppPageMode,
): ISolutionPlansProps => {
  return {
    appId: solutionDto.baseInfo.id,
    companyName: solutionDto.baseInfo.company.name,
    companyId: solutionDto.baseInfo.company.id,
    plans: solutionDto.baseInfo.pricing?.plans,
    isExternalPricing:
      solutionDto.baseInfo.pricing &&
      solutionDto.baseInfo.pricing.isExternalPricing,
    externalPricingLink:
      solutionDto.baseInfo.pricing &&
      solutionDto.baseInfo.pricing.externalPricingPageUrl,
    trialDays:
      solutionDto &&
      solutionDto.baseInfo &&
      solutionDto.baseInfo.pricing &&
      solutionDto.baseInfo.pricing.freeTrialDays,
    businessModel:
      solutionDto.baseInfo.pricing &&
      solutionDto.baseInfo.pricing.businessModel,
    currencySettings: solutionDto.baseInfo.pricing?.currencySettings,
    taxSettings: solutionDto.baseInfo.pricing?.taxSettings,
    mode,
  };
};

export interface ISolutionPlansProps
  extends Partial<WithTranslationProps>,
    Partial<InjectedExperimentsProps> {
  appId: string;
  companyName: string;
  companyId?: string;
  plans?: Plan[];
  isExternalPricing?: boolean;
  externalPricingLink?: string;
  trialDays?: string | number;
  businessModel: string;
  currencySettings?: CurrencySettings;
  taxSettings?: TaxSettings;
  mode?: AppPageMode;
}

const shouldShowPricingToggle = (plans: Plan[], showPremiumPrices) => {
  const hasMeteredBillingPlan = plans.some((plan) => plan.meteredBilling);
  if (hasMeteredBillingPlan) {
    return false;
  }
  if (!showPremiumPrices) {
    return plans?.some((plan) => plan.yearlyDiscountPercent > 0);
  }
  return plans?.some(
    (plan) =>
      plan.premiumPrices?.fullPrices?.some(
        (prices) => prices.paymentCycle?.interval?.unit === IntervalUnit.MONTH,
      ) &&
      plan.premiumPrices?.fullPrices?.some(
        (prices) => prices.paymentCycle?.interval?.unit === IntervalUnit.YEAR,
      ),
  );
};

const getInitialPricingToggleState = (
  plans: Plan[],
  showFullPrices,
  externalPricing,
) =>
  shouldShowPricingToggle(plans, showFullPrices && !externalPricing)
    ? TogglePricingValues.YEARLY
    : TogglePricingValues.NONE;

export const SolutionPlans = composer()
  .withTranslation()
  .withExperiments()
  .compose((props: ISolutionPlansProps) => {
    const [appId, setAppId] = useState<string>(props.appId);
    const trialDays = props.trialDays && Number(props.trialDays);
    const businessModelDynamic = props.businessModel === 'DYNAMIC';
    const hasMeteredBillingPlan = props.plans.some(
      (plan) => plan.meteredBilling,
    );
    const hasPlanWithPrice =
      props.plans &&
      props.plans.filter(
        (plan) => plan.isCustom === false && plan.name !== 'Free',
      ).length >= 1;

    const showFullPrices = props.experiments.enabled(
      'specs.marketplace.fullPrices',
    );
    const shouldShowReviewsFirst = props.experiments.enabled(
      'specs.marketplace.switchOrderReviewsPricing',
    );

    function getPricingCurrencyText(): string {
      const tax = props.taxSettings?.displayTaxType;
      const currency = props.currencySettings?.code;
      const companyName = props.companyName;
      const TAXES: Record<TaxSettingsTaxType, string> = {
        [TaxSettingsTaxType.GST]: 'solution.page.pricing.gst',
        [TaxSettingsTaxType.VAT]: 'solution.page.pricing.vat',
        [TaxSettingsTaxType.NOT_APPLICABLE]: 'solution.page.pricing.tax',
      };
      const taxName = props.t(TAXES[tax]);
      if (hasMeteredBillingPlan) {
        return props.t('solution.page.metered.pricingCurrency');
      }
      if (
        props.mode === AppPageMode.PREVIEW ||
        props.mode === AppPageMode.TEST
      ) {
        return props.t('solution.page.pricingCurrency');
      }
      if (!showFullPrices && props.isExternalPricing) {
        return props.t('solution.page.external.pricingCurrency', {
          currency,
          companyName,
        });
      }
      if (!showFullPrices && !props.isExternalPricing) {
        return props.t('solution.page.pricingCurrency', { currency });
      }
      if (props.isExternalPricing && props.taxSettings.showPricesWithTax) {
        return props.t('solution.page.external.pricingCurrencyTaxAll', {
          currency,
          taxName,
          companyName,
        });
      }
      if (props.isExternalPricing && !props.taxSettings.showPricesWithTax) {
        return props.t('solution.page.external.pricingCurrencyAll', {
          currency,
          companyName,
        });
      }
      if (!props.isExternalPricing && props.taxSettings.showPricesWithTax) {
        return props.t('solution.page.pricingCurrencyTaxAll', {
          currency,
          taxName,
        });
      }
      return props.t('solution.page.pricingCurrencyAll', { currency });
    }

    const { companyId } = props;
    const [togglePricing, setTogglePricing] = useState<TogglePricingValues>(
      () =>
        getInitialPricingToggleState(
          props.plans,
          showFullPrices,
          props.isExternalPricing,
        ),
    );
    const showPriceDisclaimer =
      (!businessModelDynamic && hasPlanWithPrice) || hasMeteredBillingPlan;

    const changeTogglePricing = (newValue: TogglePricingValues) => {
      biLogger.report(
        appmarketPackagePickerToggleCycleClick({
          cycle_click: newValue,
        }),
      );
      setTogglePricing(newValue);
    };

    useEffect(() => {
      if (appId !== props.appId) {
        setAppId(props.appId);
        setTogglePricing(
          getInitialPricingToggleState(
            props.plans,
            showFullPrices,
            props.isExternalPricing,
          ),
        );
      }
      if (
        togglePricing === TogglePricingValues.NONE &&
        shouldShowPricingToggle(
          props.plans,
          showFullPrices && !props.isExternalPricing,
        )
      ) {
        setTogglePricing(TogglePricingValues.YEARLY);
      }
    });

    function getExternalPricing(className) {
      return (
        <>
          {props.isExternalPricing && props.externalPricingLink ? (
            <TextButton
              dataHook="external-pricing-link"
              className={className}
              suffixIcon={<ExternalLink size="24" />}
              as="a"
              href={props.externalPricingLink}
              target="_blank"
            >
              {props.t('solution.page.comparePlans')}
            </TextButton>
          ) : null}
        </>
      );
    }

    return props.plans && props.plans.length && !isWixApp(companyId) ? (
      <div data-testid="pricing-plans-section" property="offers" typeof="Offer">
        {!shouldShowReviewsFirst ? (
          <div className={s.reviewsPricingDivider}>
            <Divider />
          </div>
        ) : null}
        <Box direction="vertical">
          <Box direction="horizontal" align="space-between">
            <div className={s.pricingPlansTitle}>
              <span>
                <Heading appearance="H2" className={s.pricingTitle}>
                  {props.t('solution.page.pricingAndPlans')}
                </Heading>
                <Text>
                  {trialDays !== 0 &&
                    props.t('solution.page.pricing.trialDays', { trialDays })}
                </Text>
              </span>
              {getExternalPricing(s.externalPricingLink)}
            </div>
          </Box>
          {shouldShowPricingToggle(
            props.plans,
            showFullPrices && !props.isExternalPricing,
          ) ? (
            <TogglePricing
              selectedValue={togglePricing}
              onClick={changeTogglePricing}
            />
          ) : null}

          <PlansLayoutMaxWidth
            plans={props.plans}
            togglePricing={togglePricing}
            currencySettings={props.currencySettings}
            taxSettings={props.taxSettings}
            mode={props.mode}
          />
          {getExternalPricing(s.externalMobile)}
          {showPriceDisclaimer && (
            <Text
              size="tiny"
              secondary
              weight="thin"
              skin="standard"
              className={s.pricingPlanCurrency}
            >
              {getPricingCurrencyText()}
            </Text>
          )}
        </Box>
        <meta property="priceCurrency" content="USD" />
        {shouldShowReviewsFirst ? (
          <div className={s.reviewsPricingDividerBottom}>
            <Divider />
          </div>
        ) : null}
      </div>
    ) : null;
  });

interface IBoxPlanProps
  extends Partial<WithTranslationProps>,
    Partial<InjectedExperimentsProps> {
  plan: Plan;
  span: number;
  togglePricing?: TogglePricingValues;
  currencySettings?: CurrencySettings;
  taxSettings?: TaxSettings;
  mode?: AppPageMode;
  hasMeteredBillingPlanWithAdditionalCharges?: boolean;
}

interface ITogglePricingProps extends Partial<WithTranslationProps> {
  selectedValue: TogglePricingValues;

  onClick(newSelection: TogglePricingValues): void;
}

const TogglePricing = composer()
  .withTranslation()
  .compose((props: ITogglePricingProps) => {
    return (
      <div className={s.pricingToggle}>
        <SegmentedToggle
          size="small"
          selected={props.selectedValue}
          onClick={(e, value: TogglePricingValues) => props.onClick(value)}
          dataHook="toggle-pricing"
        >
          <SegmentedToggle.Button
            value={TogglePricingValues.YEARLY}
            dataHook="toggle-pricing-yearly"
          >
            <Text>{props.t('solution.page.togglePricing.Yearly')}</Text>
          </SegmentedToggle.Button>
          <SegmentedToggle.Button
            value={TogglePricingValues.MONTHLY}
            dataHook="toggle-pricing-monthly"
          >
            <Text>{props.t('solution.page.togglePricing.Monthly')}</Text>
          </SegmentedToggle.Button>
        </SegmentedToggle>
      </div>
    );
  });

interface IPlansLayoutProps {
  plans: Plan[];
  togglePricing: TogglePricingValues;
  currencySettings?: CurrencySettings;
  taxSettings?: TaxSettings;
  mode?: AppPageMode;
}

const PlansLayoutMaxWidth = composer().compose((props: IPlansLayoutProps) => {
  const hasMeteredBillingPlanWithAdditionalCharges = props.plans.some(
    (plan) => plan.meteredBilling?.checkoutAdditionalInfo,
  );
  return (
    <div className={s.plansMinLayoutDynamic}>
      {props.plans.map((plan, index, array) => (
        <BoxPlanWithDynamics
          span={array.length > 3 ? 3 : 4}
          plan={plan}
          key={plan.name}
          togglePricing={props.togglePricing}
          currencySettings={props.currencySettings}
          taxSettings={props.taxSettings}
          mode={props.mode}
          hasMeteredBillingPlanWithAdditionalCharges={
            hasMeteredBillingPlanWithAdditionalCharges
          }
        />
      ))}
    </div>
  );
});

const CustomTextBoxPlan = composer()
  .withTranslation()
  .compose((props?: IBoxPlanProps) => {
    return (
      <Text dataHook="plan-custom-text" className={s.planCustomText}>
        {props.t(props.plan.customText)}
      </Text>
    );
  });

const BenefitsBoxPlan = composer()
  .withTranslation()
  .compose((props?: IBoxPlanProps) => {
    return (
      <>
        {props.plan.benefits &&
          props.plan.benefits.length > 0 &&
          props.plan.benefits.map((benefit, index, array) => (
            <PricingFeature
              key={index}
              text={benefit}
              index={index}
              last={array.length - 1 === index}
            />
          ))}
      </>
    );
  });

const PricingBoxPlan = composer()
  .withTranslation()
  .withExperiments()
  .compose((props?: IBoxPlanProps) => {
    const showFullPrices = props.experiments.enabled(
      'specs.marketplace.fullPrices',
    );
    return (
      <Box>
        <Heading appearance="H2" className={s.planPricingBoxBigDynamic}>
          {PlanPricing(
            props.plan,
            props.t,
            props.togglePricing,
            showFullPrices,
            props.currencySettings,
            props.taxSettings,
            props.mode,
          )}
        </Heading>
        <Box
          paddingBottom="3px"
          className={s.planPricingPeriodSubTitle}
          align="center"
        >
          <Text weight="bold">{planPricingPeriod(props.plan, props.t)}</Text>
        </Box>
      </Box>
    );
  });

const BoxPlanWithDynamics = composer()
  .withTranslation()
  .withExperiments()
  .compose((props?: IBoxPlanProps) => {
    const { isMobile } = useConfigContext();
    const pricingPlanName = props.plan.name || '';
    const isDynamicPlan = props.plan.isCustom;
    const isCustomText =
      props.plan?.planDescription === PlanDescription.CUSTOM_TEXT;
    const showFullPrices = props.experiments.enabled(
      'specs.marketplace.fullPrices',
    );

    return (
      <Card className={s.pricingPlanContainerDynamic}>
        <Box
          marginTop="30px"
          marginRight="24px"
          marginBottom="24px"
          marginLeft="24px"
        >
          <Box direction="vertical">
            <Box marginBottom="6px">
              {isMobile ? (
                <Heading appearance="H6">
                  {props.t('solution.page.planType', {
                    planType: props.plan.name,
                  })}
                </Heading>
              ) : (
                <Text
                  size="small"
                  weight="normal"
                  dataHook={`pricing-plan-${pricingPlanName.toLowerCase()}`}
                  secondary
                >
                  {props.t('solution.page.planType', {
                    planType: props.plan.name,
                  })}
                </Text>
              )}
            </Box>
            <Box
              gap={
                isMobile
                  ? '12px'
                  : !props.plan.meteredBilling?.checkoutAdditionalInfo &&
                    props.hasMeteredBillingPlanWithAdditionalCharges
                  ? `48px`
                  : '24px'
              }
              direction="vertical"
            >
              <Box direction="vertical" verticalAlign="bottom">
                {isDynamicPlan ? (
                  <Box>
                    <Heading appearance="H2">
                      {props.t(props.plan.title)}
                    </Heading>
                  </Box>
                ) : showFullPrices ? (
                  <PricingBoxPlanNew {...props}></PricingBoxPlanNew>
                ) : (
                  <PricingBoxPlan {...props}></PricingBoxPlan>
                )}
                {props.plan.meteredBilling?.checkoutAdditionalInfo && (
                  <Text
                    size={isMobile ? 'small' : 'medium'}
                    secondary
                    id="additional-info"
                  >
                    {getAdditionalCharges(props)}
                  </Text>
                )}
              </Box>
              <Box direction="vertical" align="left">
                {isCustomText ? (
                  <CustomTextBoxPlan {...props} />
                ) : (
                  <BenefitsBoxPlan {...props} />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Card>
    );
  });

const getAdditionalCharges = (props) => {
  let additionalCharges = props.plan?.meteredBilling?.checkoutAdditionalInfo;
  const additionalChargesWithCurrency =
    props.plan?.additionalChargesWithCurrency;
  const additionalChargesFirstPart = additionalCharges?.split('{')[0];
  const additionalChargesSecondPart = additionalCharges?.split('}')[1];

  if (additionalChargesWithCurrency) {
    const [currencyToString] = usePremiumCurrencyFormatter({
      currencySettings: {
        [props.currencySettings?.code]: {
          symbol: props.currencySettings?.symbol,
          decimalSep: props.currencySettings?.decimalSeparator,
          groupSep: props.currencySettings?.groupSeparator,
          negPrefix: props.currencySettings?.negativePrefix,
          negSuffix: props.currencySettings?.negativeSuffix,
          posPrefix: '¤',
          posSuffix: '',
          fractionSize: 2,
        },
      },
      currencyCode: props.currencySettings?.code,
    });
    const price = currencyToString(Number(additionalChargesWithCurrency));
    additionalCharges = `${additionalChargesFirstPart}${price}${additionalChargesSecondPart}`;
  }
  return (
    <span
      data-hook="solution-plan-metered-additional-charges"
      id="additional-info"
    >
      {additionalCharges}
    </span>
  );
};

const PricingBoxPlanNewSmall = composer()
  .withTranslation()
  .withExperiments()
  .compose((props?: IBoxPlanProps) => {
    const { isMobile } = useConfigContext();
    const showFullPrices = props.experiments.enabled(
      'specs.marketplace.fullPrices',
    );
    const price = GetPrice(
      props.plan,
      props.t,
      props.togglePricing,
      showFullPrices,
      props.currencySettings,
      props.taxSettings,
      props.mode,
    );

    if (price === '0') {
      return (
        <Box
          paddingBottom={
            props.plan.meteredBilling?.checkoutAdditionalInfo
              ? '3px'
              : undefined
          }
        >
          <Heading
            appearance="H2"
            className={s.planPricingBoxSmallDynamicNewFree}
            dataHook="free-to-install"
          >
            <meta property="price" content="0" />
            {props.plan.meteredBilling
              ? props.t('solution.page.metered.billing.freePlanTitle')
              : props.t('solution.page.freePlanTitle')}
          </Heading>
          <Box
            paddingBottom="3px"
            className={s.planPricingPeriodSubTitle}
            align="center"
          >
            <Text weight="bold">{planPricingPeriod(props.plan, props.t)}</Text>
          </Box>
        </Box>
      );
    }
    const divider = price?.includes('.') ? '.' : ',';
    const priceParts = price?.split(divider);
    const decodedSign = priceParts?.[0].includes('$')
      ? '$'
      : props?.currencySettings?.symbol?.replace(
          /&#(\d+);/g,
          function (match, dec) {
            return String.fromCharCode(dec);
          },
        );
    const part1 = priceParts?.[0]?.split(decodedSign)?.[1];
    const part2 = priceParts?.[1] || '00';

    return (
      <Box marginTop={isMobile ? '2px' : '6px'} height="23px">
        <Box marginTop="-1px" marginRight="4px">
          <Text size={isMobile ? 'tiny' : 'small'}>{decodedSign}</Text>
        </Box>
        <Heading
          appearance="H2"
          className={s.planPricingBoxSmallDynamicNew}
          dataHook="plan-price"
        >
          {part1}
        </Heading>
        <Box marginTop={isMobile ? '3px' : '1px'} marginLeft="2px">
          <Text
            className={s.planPricingBoxBigDynamicSecondPart}
            weight={isMobile ? 'bold' : 'normal'}
            size="tiny"
          >
            {part2}
          </Text>
        </Box>
        <Box
          marginBottom="3px"
          marginTop={isMobile ? '0' : '2px'}
          marginRight="10px"
          marginLeft="2px"
          align="center"
        >
          <Text
            weight={isMobile ? 'bold' : 'normal'}
            size={isMobile ? 'small' : 'medium'}
          >
            {planPricingPeriod(props.plan, props.t)}
          </Text>
        </Box>
      </Box>
    );
  });

const PricingBoxPlanNew = composer().compose((props?: IBoxPlanProps) => {
  return <PricingBoxPlanNewSmall {...props}></PricingBoxPlanNewSmall>;
});

const GetPrice = (
  { yearlyPrice, monthlyPrice, premiumPrices, meteredBilling }: Plan,
  t,
  togglePricing: TogglePricingValues = TogglePricingValues.NONE,
  showFullPrices: boolean,
  currencySettings?: CurrencySettings,
  taxSettings?: TaxSettings,
  mode?: AppPageMode,
) => {
  const hasPrice = meteredBilling
    ? meteredBilling.monthlyBaseFee
    : yearlyPrice || monthlyPrice;
  if (!hasPrice) {
    return '0';
  }
  const [currencyToString] = usePremiumCurrencyFormatter({
    currencySettings: {
      [currencySettings?.code]: {
        symbol: currencySettings?.symbol,
        decimalSep: currencySettings?.decimalSeparator,
        groupSep: currencySettings?.groupSeparator,
        negPrefix: currencySettings?.negativePrefix,
        negSuffix: currencySettings?.negativeSuffix,
        posPrefix: '¤',
        posSuffix: '',
        fractionSize: 2,
      },
    },
    currencyCode: currencySettings?.code,
  });
  const showPremium =
    showFullPrices &&
    premiumPrices &&
    mode !== AppPageMode.TEST &&
    mode !== AppPageMode.PREVIEW;

  const getPremiumPrice = (intervalUnit) => {
    const premiumPrice = premiumPrices?.fullPrices.find(
      (prices) => prices.paymentCycle?.interval?.unit === intervalUnit,
    );
    const relevantPremiumPrice = taxSettings?.showPricesWithTax
      ? Number(premiumPrice?.monthlyPriceWithTax)
      : Number(premiumPrice?.monthlyPriceBeforeTax);

    return currencyToString(relevantPremiumPrice);
  };

  const getDCPrice = (unitPrice) => {
    let dcPrice;
    if (
      showFullPrices &&
      mode !== AppPageMode.TEST &&
      mode !== AppPageMode.PREVIEW
    ) {
      dcPrice = currencyToString(Number(unitPrice));
    } else {
      dcPrice = `$${unitPrice}`;
    }
    return dcPrice;
  };

  const getMonthOrYear = () => {
    function getInterval() {
      return premiumPrices?.fullPrices.some(
        (plan) => plan.paymentCycle?.interval?.unit === IntervalUnit.MONTH,
      )
        ? IntervalUnit.MONTH
        : IntervalUnit.YEAR;
    }

    switch (togglePricing) {
      case TogglePricingValues.YEARLY:
        return showPremium ? IntervalUnit.YEAR : yearlyPrice;
      case TogglePricingValues.MONTHLY:
        return showPremium
          ? IntervalUnit.MONTH
          : meteredBilling
          ? meteredBilling.monthlyBaseFee
          : monthlyPrice;
      case TogglePricingValues.NONE:
        return showPremium
          ? getInterval()
          : meteredBilling
          ? meteredBilling.monthlyBaseFee
          : monthlyPrice;
      default:
        return '';
    }
  };

  const price = showPremium
    ? getPremiumPrice(getMonthOrYear())
    : getDCPrice(getMonthOrYear());
  return price;
};

const PlanPricing = (
  { yearlyPrice, monthlyPrice, premiumPrices, meteredBilling }: Plan,
  t,
  togglePricing: TogglePricingValues = TogglePricingValues.NONE,
  showFullPrices: boolean,
  currencySettings?: CurrencySettings,
  taxSettings?: TaxSettings,
  mode?: AppPageMode,
) => {
  const hasPrice = meteredBilling
    ? meteredBilling.monthlyBaseFee
    : yearlyPrice || monthlyPrice;
  if (!hasPrice) {
    return (
      <>
        <meta property="price" content="0" />
        {t('solution.page.freePlanTitle')}
      </>
    );
  }
  const [currencyToString] = usePremiumCurrencyFormatter({
    currencySettings: {
      [currencySettings?.code]: {
        symbol: currencySettings?.symbol,
        decimalSep: currencySettings?.decimalSeparator,
        groupSep: currencySettings?.groupSeparator,
        negPrefix: currencySettings?.negativePrefix,
        negSuffix: currencySettings?.negativeSuffix,
        posPrefix: '¤',
        posSuffix: '',
        fractionSize: 2,
      },
    },
    currencyCode: currencySettings?.code,
  });
  const showPremium =
    showFullPrices &&
    premiumPrices &&
    mode !== AppPageMode.TEST &&
    mode !== AppPageMode.PREVIEW;

  const getPremiumPrice = (intervalUnit) => {
    const premiumPrice = premiumPrices?.fullPrices.find(
      (prices) => prices.paymentCycle?.interval?.unit === intervalUnit,
    );
    const relevantPremiumPrice = taxSettings?.showPricesWithTax
      ? premiumPrice?.monthlyPriceWithTax
      : premiumPrice?.monthlyPriceBeforeTax;
    return currencyToString(Number(relevantPremiumPrice));
  };

  const getDCPrice = (unitPrice) => {
    let dcPrice;
    if (
      showFullPrices &&
      mode !== AppPageMode.TEST &&
      mode !== AppPageMode.PREVIEW
    ) {
      dcPrice = currencyToString(Number(unitPrice));
    } else {
      dcPrice = `$${unitPrice}`;
    }
    return dcPrice;
  };

  const getMonthOrYear = () => {
    function getInterval() {
      return premiumPrices?.fullPrices.some(
        (plan) => plan.paymentCycle?.interval?.unit === IntervalUnit.MONTH,
      )
        ? IntervalUnit.MONTH
        : IntervalUnit.YEAR;
    }

    switch (togglePricing) {
      case TogglePricingValues.YEARLY:
        return showPremium ? IntervalUnit.YEAR : yearlyPrice;
      case TogglePricingValues.MONTHLY:
        return showPremium ? IntervalUnit.MONTH : monthlyPrice;
      case TogglePricingValues.NONE:
        return showPremium ? getInterval() : monthlyPrice;
      default:
        return '';
    }
  };

  return (
    <>
      <span data-hook="solution-plan-price" property="price">
        {showPremium
          ? getPremiumPrice(getMonthOrYear())
          : meteredBilling
          ? meteredBilling.monthlyBaseFee.toString()
          : getDCPrice(getMonthOrYear())}
      </span>
    </>
  );
};

const planPricingPeriod = (
  { yearlyPrice, monthlyPrice, meteredBilling }: Plan,
  t,
) => {
  const planMonthlyPrice = meteredBilling
    ? meteredBilling?.monthlyBaseFee
    : monthlyPrice;
  return yearlyPrice || planMonthlyPrice
    ? `  /${t('solution.page.pricingMonth')}`
    : '';
};
const PricingFeature = ({ text, index, last }) => {
  return (
    <Box
      key={index}
      align="center"
      direction="horizontal"
      verticalAlign="middle"
      marginBottom={last ? '' : '9px'}
      inline
    >
      <div className={s.stylingBenefit}>
        <div className={s.stylingConfirm}>
          <ConfirmSmall />
        </div>
        <Text
          dataHook={`pricefeature-${text}`}
          className={s.benefitTextBigBoxDynamic}
        >
          {text}
        </Text>
      </div>
    </Box>
  );
};
